import * as React from 'react';
import { Button, Stack } from 'react-bootstrap';
import { Link, useParams } from 'react-router-dom';
import { BookingPaymentTerms, EventProfile, Event } from 'cb-model';
import {
  Display,
  getConfigOrThrow,
  IsAllowed,
  List,
  NavIconContainer,
  PageTitle,
  QueryMulti,
  Section,
  useCacheRecord,
} from 'gossamer-front-end';
import { getFilter } from 'gossamer-unified';
import { getBookingPaymentTermsText } from 'cb-unified';
import { EventProfileCard } from './EventProfileCard';

export const EventProfileDisplay = (): React.JSX.Element => {
  const params = useParams();
  const eventProfileRecord = useCacheRecord(EventProfile, String(params.key), !params.key);
  const bookingPaymentTermsRecord = useCacheRecord(
    BookingPaymentTerms,
    String(eventProfileRecord.data?.booking_payment_terms_id),
    !eventProfileRecord.data?.booking_payment_terms_id,
  );
  return (
    <>
      <NavIconContainer entityId={EventProfile.id} />
      <PageTitle verb="Display" entity={EventProfile} keyValue={params.key} subtitle="Details of an Event Profile" />
      {!!eventProfileRecord.data && (
        <div>
          <EventProfileCard profile={eventProfileRecord.data} />
          <br />
        </div>
      )}
      <Section>
        {!!eventProfileRecord.data && (
          <>
            <h6>Basic Details</h6>
            <Display entity={EventProfile} keyValue={params.key} row={eventProfileRecord.data} fieldGroup="basic" />
            <h6>Event Engagement Settings</h6>
            <Display
              entity={EventProfile}
              keyValue={params.key}
              row={eventProfileRecord.data}
              fieldGroup="engagement"
            />
            <h6>Audience Settings</h6>
            <Display entity={EventProfile} keyValue={params.key} row={eventProfileRecord.data} fieldGroup="audience" />
            <h6>Post-Event Flow Settings</h6>
            <Display
              entity={EventProfile}
              keyValue={params.key}
              row={eventProfileRecord.data}
              fieldGroup="post-event-flow"
            />
            <h6>Certificate Templates</h6>
            <Display
              entity={EventProfile}
              keyValue={params.key}
              row={eventProfileRecord.data}
              fieldGroup="certificates"
            />
          </>
        )}
        <Stack direction="horizontal" gap={2}>
          <IsAllowed entity={EventProfile} action="update">
            <Link to={`/admin/event-profile/update/${params.key}`}>
              <Button size="sm" variant="primary">
                Update
              </Button>
            </Link>
          </IsAllowed>
          <a
            download
            href={`${getConfigOrThrow('gossamer-be-origin')}/gossamer/main/certificate-check/${params.key}`}
            title={'Download Example Certificate'}
          >
            Download Example Attendance Certificate
          </a>
          {!!eventProfileRecord.data?.host_certificate_file && (
            <a
              download
              href={`${getConfigOrThrow('gossamer-be-origin')}/gossamer/main/host-certificate-check/${params.key}`}
              title={'Download Example Host Certificate'}
            >
              Download Example Host Certificate
            </a>
          )}
        </Stack>
      </Section>
      <Section title="Booking Payment Terms" subtitle="associated with this Profile">
        <p>In order to create paid events, you need to define the cancellation policy.</p>
        <ul>
          <li>
            You can choose to offer a refund of the ticket price (excluding booking fees) if the user cancels their
            booking earlier than a certain number of days or hours before the event is due to start,
          </li>
          <li>or you can choose to offer no cancellation at all.</li>
          <li>
            You can also amend the text displayed to the user when they make their booking. The raw text must contain
            either 'CANX_ALLOWED_HOURS_BEFORE_START' or 'CANX_ALLOWED_DAYS_BEFORE_START' which will be replaced with the
            relevant number of hours or days in the user-facing text.
          </li>
          <li> All the events you create with this Event Profile will use the same cancellation policy.</li>
        </ul>

        {bookingPaymentTermsRecord.data ? (
          <p>
            Current user-facing text: <b>{getBookingPaymentTermsText(bookingPaymentTermsRecord.data)}</b>
          </p>
        ) : (
          <p>No Booking Payment Terms associated yet.</p>
        )}
        <IsAllowed entity={BookingPaymentTerms} action="update">
          <Link to={`/admin/event-profile/update-payment-terms/${params.key}`}>
            <Button size="sm" variant="primary">
              Create/Update
            </Button>
          </Link>
        </IsAllowed>
      </Section>
      <Section title="Events" subtitle="using this Profile">
        <QueryMulti
          entity={Event}
          alterEntity={(localEntity: typeof Event) => {
            localEntity.fields.event_profile_id.listColumn = false;
          }}
          queryOptions={{
            where: {
              and: [getFilter('event_profile_id', 'eq', params.key)],
            },
          }}
          render={List}
        />
      </Section>
    </>
  );
};
